import { create } from 'zustand';
import { RoomCategory, Room, Booking } from '../types';
import { bookingsService } from '../services/firestore/bookings';
import { roomsService } from '../services/firestore/rooms';

interface State {
  roomCategories: RoomCategory[];
  rooms: Room[];
  bookings: Booking[];
  setRoomCategories: (categories: RoomCategory[]) => void;
  setRooms: (rooms: Room[]) => void;
  addBooking: (booking: Omit<Booking, 'id'>) => Promise<void>;
  updateBooking: (bookingId: string, updates: Partial<Booking>) => Promise<void>;
  cancelBooking: (bookingId: string) => Promise<void>;
  fetchRoomCategories: () => Promise<void>;
  fetchBookings: () => Promise<void>;
  uploadDefaultData: () => Promise<void>;
}

const defaultRoomCategories = [
  {
    name: 'Deluxe Room',
    description: 'Spacious room with a king-size bed and city view',
    pricePerNight: 15000,
    maxGuests: 2,
    amenities: [
      'King-size bed',
      'City view',
      'Free WiFi',
      '55" Smart TV',
      'Mini Bar',
      'Air Conditioning',
      'Room Service',
      'En-suite bathroom',
      'Premium toiletries'
    ],
    facilities: [
      'Access to fitness center',
      'Pool access',
      'Complimentary breakfast',
      '24/7 concierge service'
    ],
    imageUrl: 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&q=80&w=800',
  },
  {
    name: 'Executive Suite',
    description: 'Luxury suite with separate living area and ocean view',
    pricePerNight: 25000,
    maxGuests: 4,
    amenities: [
      'King-size bed',
      'Ocean view',
      'Separate living room',
      'Private balcony',
      'Free WiFi',
      '65" Smart TV',
      'Mini Bar',
      'Air Conditioning',
      'Kitchenette',
      'Premium toiletries',
      'Walk-in closet'
    ],
    facilities: [
      'Executive lounge access',
      'Priority check-in/out',
      'Complimentary breakfast',
      'Free parking',
      'Spa access',
      'Butler service'
    ],
    imageUrl: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=80&w=800',
  },
  {
    name: 'Family Suite',
    description: 'Perfect for families with two queen beds and entertainment area',
    pricePerNight: 20000,
    maxGuests: 4,
    amenities: [
      'Two queen beds',
      'Garden view',
      'Entertainment area',
      'Free WiFi',
      '55" Smart TV',
      'Mini Bar',
      'Air Conditioning',
      'Extra storage',
      'Children\'s amenities',
      'Family bathroom'
    ],
    facilities: [
      'Kids club access',
      'Family pool access',
      'Complimentary breakfast',
      'Game room access',
      'Babysitting service available'
    ],
    imageUrl: 'https://images.unsplash.com/photo-1566665797739-1674de7a421a?auto=format&fit=crop&q=80&w=800',
  },
];

export const useStore = create<State>((set: any, get: any) => ({
  roomCategories: [],
  rooms: [],
  bookings: [],
  
  setRoomCategories: (categories) => set({ roomCategories: categories }),
  setRooms: (rooms) => set({ rooms }),
  
  addBooking: async (booking) => {
    try {
      const bookingId = await bookingsService.createBooking(booking);
      set((state: any) => ({
        bookings: [...state.bookings, { ...booking, id: bookingId }],
      }));
    } catch (error) {
      console.error('Error adding booking:', error);
      throw error;
    }
  },
  
  updateBooking: async (bookingId, updates) => {
    try {
      await bookingsService.updateBooking(bookingId, updates);
      set((state: any) => ({
        bookings: state.bookings.map((booking: any) =>
          booking.id === bookingId ? { ...booking, ...updates } : booking
        ),
      }));
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    }
  },
  
  cancelBooking: async (bookingId) => {
    try {
      await bookingsService.updateBooking(bookingId, { status: 'cancelled' as const });
      set((state: any) => ({
        bookings: state.bookings.map((booking: any) =>
          booking.id === bookingId
            ? { ...booking, status: 'cancelled' as const }
            : booking
        ),
      }));
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw error;
    }
  },
  
  fetchRoomCategories: async () => {
    try {
      const categories = await roomsService.getRoomCategories();
      set({ roomCategories: categories });
    } catch (error) {
      console.error('Error fetching room categories:', error);
      throw error;
    }
  },
  
  fetchBookings: async () => {
    try {
      const bookings = await bookingsService.getBookings();
      set({ bookings });
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw error;
    }
  },
  
  uploadDefaultData: async () => {
    try {
      await roomsService.uploadDefaultData(defaultRoomCategories);
      await get().fetchRoomCategories();
    } catch (error) {
      console.error('Error uploading default data:', error);
      throw error;
    }
  },
}));
